import React from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Leaf } from "lucide-react";

const Fade = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const services = [
  { title: "Fertility Assessment", desc: "Personalized nutrition & cycle mapping for conception" },
  { title: "PCOS & Hormonal Health", desc: "Balancing hormones and supporting egg quality" },
  { title: "Male Fertility", desc: "Targeted nutrition for sperm health & motility" },
  { title: "IVF Preparation", desc: "Nutritional support before, during & after IVF" },
  { title: "Pregnancy Nutrition", desc: "Trimester-wise plans & gestational health" },
  { title: "Family Wellness", desc: "Pediatric, perimenopause & elderly care" },
];

const BG_IMAGE = "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1920&q=80";

const ServicesSection = () => (
  <section
    id="services"
    data-testid="services-section"
    className="relative overflow-hidden"
  >
    {/* Full-section food background */}
    <div className="absolute inset-0">
      <img
        src={BG_IMAGE}
        alt=""
        className="w-full h-full object-cover"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-[#0D1F17]/[0.88]" />
    </div>

    {/* Subtle dot pattern over the image */}
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage: "radial-gradient(circle, #D4AF37 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }}
    />

    <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10 py-20 md:py-28">
      {/* Header */}
      <Fade>
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-[#D4AF37]">
            <Leaf size={12} />
            What We Do
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#FEFDFB] tracking-tight leading-none mt-4">
            Your Journey,{" "}
            <span className="italic font-light text-[#D4AF37]">Simplified</span>
          </h2>
          <p className="text-[#FEFDFB]/50 text-base md:text-lg max-w-xl mx-auto mt-4 leading-relaxed">
            From conception planning to family wellness — evidence-based
            nutrition at every stage.
          </p>
        </div>
      </Fade>

      {/* Desktop: 3-column service items */}
      <div className="hidden md:grid grid-cols-3 gap-6">
        {services.map((s, i) => (
          <Fade key={s.title} delay={i * 0.08}>
            <div
              className="group p-6 rounded-2xl bg-white/[0.06] backdrop-blur-md border border-[#FEFDFB]/[0.08] hover:border-[#D4AF37]/30 hover:bg-white/[0.1] transition-all duration-500"
              data-testid={`service-item-${i}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mb-4 border border-[#D4AF37]/20">
                <Leaf size={16} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl text-[#D4AF37] italic tracking-tight leading-snug">
                {s.title}
              </h3>
              <div className="h-px bg-[#D4AF37]/20 w-10 mt-3 mb-3 group-hover:w-16 transition-all duration-500" />
              <p className="text-[#FEFDFB]/55 text-sm leading-relaxed">{s.desc}</p>
            </div>
          </Fade>
        ))}
      </div>

      {/* Mobile: compact list */}
      <div className="md:hidden space-y-3">
        {services.map((s, i) => (
          <Fade key={s.title} delay={i * 0.05}>
            <div
              className="group flex items-center gap-4 p-4 rounded-xl bg-white/[0.06] backdrop-blur-md border border-[#FEFDFB]/[0.08]"
              data-testid={`service-item-mobile-${i}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/20">
                <Leaf size={14} className="text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-serif text-lg text-[#D4AF37] italic tracking-tight">{s.title}</h3>
                <p className="text-[#FEFDFB]/50 text-sm leading-relaxed mt-0.5">{s.desc}</p>
              </div>
            </div>
          </Fade>
        ))}
      </div>

      {/* CTA */}
      <Fade delay={0.3}>
        <div className="text-center mt-14">
          <a
            href="/consultation"
            data-testid="services-cta"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0D1F17] px-10 py-3.5 text-xs tracking-[0.12em] uppercase font-bold hover:bg-[#E8D48B] transition-colors duration-300 rounded-full"
          >
            Book a Consultation <ArrowRight size={14} />
          </a>
        </div>
      </Fade>
    </div>
  </section>
);

export default ServicesSection;
